import { useCallback } from 'react';

import type { LoyaltyEnrollmentIntegration, LoyaltyEnrollmentSurface } from 'src/utils/analytics/events';
import { tracker } from 'src/utils/analytics';

type TrackFailedArgs = {
  errorCode?: string;
  errorMessage?: string;
};

export type UseLoyaltyEnrollmentAnalyticsOptions = {
  surface: LoyaltyEnrollmentSurface | null;
  integration: LoyaltyEnrollmentIntegration | undefined;
};

export type UseLoyaltyEnrollmentAnalyticsReturn = {
  trackViewed: () => void;
  trackSubmitted: () => void;
  trackSucceeded: () => void;
  trackFailed: (args?: TrackFailedArgs) => void;
};

// `integration` is detected asynchronously at some call sites (it depends on dispensary data
// loading), so callers pass `undefined` while it's unknown. Track calls made before it resolves
// are dropped rather than firing with a fabricated value, since the schema requires a real
// Integrations value. `surface` is `null` for call sites that deliberately opt out of this
// event entirely (e.g. flows instrumented elsewhere); track calls are dropped in that case too.
export const useLoyaltyEnrollmentAnalytics = ({
  surface,
  integration,
}: UseLoyaltyEnrollmentAnalyticsOptions): UseLoyaltyEnrollmentAnalyticsReturn => {
  const track = useCallback(
    (action: 'failed' | 'submitted' | 'succeeded' | 'viewed', errorFields: TrackFailedArgs = {}): void => {
      if (integration === undefined || surface === null) {
        return;
      }
      tracker.loyaltyEnrollmentAction({ surface, integration, action, ...errorFields });
    },
    [surface, integration]
  );

  const trackViewed = useCallback((): void => track('viewed'), [track]);

  const trackSubmitted = useCallback((): void => track('submitted'), [track]);

  const trackSucceeded = useCallback((): void => track('succeeded'), [track]);

  const trackFailed = useCallback(
    ({ errorCode, errorMessage }: TrackFailedArgs = {}): void => track('failed', { errorCode, errorMessage }),
    [track]
  );

  return {
    trackViewed,
    trackSubmitted,
    trackSucceeded,
    trackFailed,
  };
};
